import React, { useState, useEffect } from 'react';
import api from '../../../utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { showToast } from '../../../Components/ToastNotifications';

const ClassificationsAdd = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const batchId = searchParams.get('batchId') || '';
  const institutionId = searchParams.get('institutionId') || '';
  const divisionInstitutionsId = searchParams.get('divisionInstitutionId') || '';
  const backQueryString = `?batchId=${batchId}&institutionId=${institutionId}&divisionInstitutionId=${divisionInstitutionsId}`;

  const { register, setValue, handleSubmit, watch } = useForm({
    defaultValues: {
      classifications: [
        { classification: '', classificationTypeId: '' },
        { classification: '', classificationTypeId: '' },
        { classification: '', classificationTypeId: '' },
        { classification: '', classificationTypeId: '' },
        { classification: '', classificationTypeId: '' },
      ],
    },
  });

  const classifications = watch('classifications');

  const [isLoading, setIsloading] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [types, setTypes] = useState([]);

  // Type options depend on the batch's formula version (v1 -> PAT/CB/ATT, v2 -> CB)
  useEffect(() => {
    if (!batchId) return;
    const getTypes = async () => {
      try {
        const data = await api.getClassificationFinalTypes({ domain: 'HS', batchId });
        setTypes(data || []);
      } catch (error) {
        console.error('Failed to get classification types:', error);
      }
    };
    getTypes();
  }, [batchId]);

  // KPI HS untuk batch formula v2 dikelola terpusat oleh Admin — mentor hanya boleh
  // menambah KPI HS baru untuk batch formula v1 (lihat juga gating di halaman List).
  const isV2Batch = !!batchId && types.length > 0;

  useEffect(() => {
    if (!divisionInstitutionsId) {
      setIsDataLoaded(true);
      return;
    }
    const getInstitutions = async () => {
      try {
        const data = await api.getClassificationsHardskillByDivisiLembagaId({
          id: divisionInstitutionsId,
        });

        if (data?.institutions) {
          setValue('institutionsName', data?.institutions?.institutionsName);
          setValue('divisionName', data?.institutions?.divisionName);
          setValue('batch', data?.batch?.batch);
        }
        setIsDataLoaded(true);
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setLoadError('Gagal memuat data divisi. Silakan coba lagi.');
        setIsDataLoaded(true);
      }
    };

    getInstitutions();
  }, [divisionInstitutionsId, setValue]);

  // Check if EVERY input is filled and has content
  const isFormValid =
    !isLoading &&
    !isV2Batch &&
    Array.isArray(classifications) &&
    classifications.length === 5 &&
    classifications.every((val) => val?.classification && val.classification.trim() !== '');

  const onSubmit = async (data) => {
    if (isLoading) return;
    setIsloading(true);
    try {
      const payload = {
        divisionInstitutionsId,
        classifications: (data.classifications || []).map((item) => ({
          classification: item.classification,
          classificationTypeId: item.classificationTypeId
            ? parseInt(item.classificationTypeId, 10)
            : null,
        })),
      };
      await api.addClassifications({ payload });
      showToast('KPI berhasil disimpan!', 'success');
      navigate(`/mentor/classifications${backQueryString}`);
    } catch (error) {
      console.error('Submission failed:', error);
      showToast(
        error.message || 'Gagal menambahkan KPI. Silakan coba lagi.',
        'danger',
      );
    } finally {
      setIsloading(false);
    }
  };

  if (!isDataLoaded) {
    return (
      <div className='d-flex justify-content-center align-items-center py-5'>
        <div
          className='spinner-border text-primary'
          role='status'
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  // Divisi belum dipilih dari halaman List — dropdown sudah dihilangkan dari halaman ini,
  // jadi arahkan mentor kembali untuk memilih Batch/Lembaga/Divisi terlebih dahulu.
  if (!divisionInstitutionsId) {
    return (
      <div className='card card-body border-0 shadow-sm rounded-4 text-center py-5'>
        <div className='text-muted mb-3'>
          <i className='bi bi-diagram-3 fs-1 opacity-25'></i>
        </div>
        <h6 className='fw-bold text-dark mb-1'>Divisi Belum Dipilih</h6>
        <p
          className='text-muted small mb-4 mx-auto'
          style={{ maxWidth: '340px' }}
        >
          Pilih Batch, Lembaga, dan Divisi terlebih dahulu di halaman KPI sebelum
          menambahkan KPI baru.
        </p>
        <div>
          <button
            type='button'
            onClick={() => navigate('/mentor/classifications')}
            className='btn btn-primary rounded-pill px-4 fw-bold'
          >
            <i className='bi bi-arrow-left me-2'></i>
            Kembali ke Daftar KPI
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='card card-body border-0 shadow-sm rounded-4'>
      {/* Header with Back Button */}
      <div className='d-flex align-items-center gap-3 mb-4'>
        <button
          onClick={() => navigate(`/mentor/classifications${backQueryString}`)}
          className='btn btn-light rounded-circle shadow-none border d-flex align-items-center justify-content-center hover-translate-y transition-all'
          style={{ width: '40px', height: '40px' }}
        >
          <i className='bi bi-arrow-left'></i>
        </button>
        <div>
          <span className='badge bg-success bg-opacity-10 text-success px-3 py-1 rounded-pill fw-bold small mb-1'>
            KPI Management
          </span>
          <h1 className='h4 fw-bold mb-0 text-dark'>Tambah KPI Hardskill</h1>
        </div>
      </div>

      {loadError && (
        <div
          className='alert alert-danger border-0 shadow-sm rounded-4 mb-4 d-flex align-items-center gap-3'
          role='alert'
        >
          <i className='bi bi-exclamation-triangle-fill fs-5'></i>
          <div className='flex-grow-1'>{loadError}</div>
        </div>
      )}

      {isV2Batch && (
        <div
          className='alert alert-warning border-0 shadow-sm rounded-4 mb-4 d-flex align-items-center gap-3'
          role='alert'
        >
          <i className='bi bi-exclamation-triangle-fill fs-5'></i>
          <div className='flex-grow-1 small'>
            Batch ini memakai formula <strong>v2</strong> dengan struktur KPI HS baku
            (termasuk Capacity Building otomatis) yang dikelola terpusat oleh <strong>Admin</strong>.
            Mentor hanya dapat menambah KPI HS baru untuk batch formula <strong>v1</strong>.
          </div>
        </div>
      )}

      <form
        onSubmit={handleSubmit(onSubmit)}
        className='needs-validation'
        noValidate
      >
        {/* Info Card - Compact (read-only, dipilih sebelumnya dari halaman List) */}
        <div className='card border border-light-subtle bg-light mb-4 shadow-none'>
          <div className='card-body p-3'>
            <div className='row align-items-center g-3'>
              <div className='col-md-5'>
                <div className='d-flex flex-column'>
                  <span className='text-muted small fw-medium'>Lembaga</span>
                  <span className='fw-bold text-dark'>
                    {watch('institutionsName')}
                  </span>
                </div>
              </div>
              <div className='col-md-5'>
                <div className='d-flex flex-column'>
                  <span className='text-muted small fw-medium'>Divisi</span>
                  <span className='fw-bold text-dark'>
                    {watch('divisionName')}
                  </span>
                </div>
              </div>
              <div className='col-md-2 text-md-end text-start border-start border-light-subtle ps-md-4'>
                <div className='d-flex flex-column'>
                  <span className='text-muted small fw-medium text-nowrap'>
                    Batch Program
                  </span>
                  <span className='badge bg-white text-dark border px-3 py-1 align-self-md-end mt-1'>
                    {watch('batch')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Sections */}
        <div className='row g-4'>
          {[0, 1, 2, 3, 4].map((key) => (
            <div
              key={key}
              className='col-12'
            >
              <div className='card border border-light-subtle border-start border-2 border-success bg-white p-4 rounded-4 transition-all shadow-none'>
                <div className='d-flex align-items-center justify-content-between mb-3'>
                  <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                    <span
                      className='bg-success text-white rounded-circle d-flex align-items-center justify-content-center'
                      style={{
                        width: '24px',
                        height: '24px',
                        fontSize: '12px',
                      }}
                    >
                      {key + 1}
                    </span>
                    Indikator Kompetensi Baru
                  </h6>
                  <select
                    {...register(`classifications.${key}.classificationTypeId`)}
                    className='form-select form-select-sm w-auto'
                    disabled={isV2Batch}
                  >
                    <option value=''>Manual</option>
                    {types.map((type) => (
                      <option
                        key={type.id}
                        value={type.id}
                      >
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <textarea
                  {...register(`classifications.${key}.classification`, { required: true })}
                  rows={2}
                  className='form-control bg-light border-0 shadow-none'
                  placeholder='Masukkan deskripsi KPI baru di sini...'
                  required
                  disabled={isV2Batch}
                  style={{ resize: 'none' }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Action Button */}
        <div className='mt-5 d-flex justify-content-end'>
          <button
            type='submit'
            disabled={!isFormValid}
            className={`btn px-5 py-2 fw-bold rounded-pill transition-all d-flex align-items-center gap-2 ${
              isFormValid ? 'btn-success' : 'btn-secondary opacity-50'
            }`}
          >
            {isLoading ? (
              <>
                <span
                  className='spinner-border spinner-border-sm'
                  aria-hidden='true'
                ></span>
                Menyimpan...
              </>
            ) : (
              <>
                <i className='bi bi-plus-circle'></i>
                Simpan KPI Baru
              </>
            )}
          </button>
        </div>
      </form>

      <style>{`
        .hover-translate-y:hover { transform: translateY(-3px); }
        .transition-all { transition: all 0.2s ease; }
        .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .card { border-radius: 1rem; }
      `}</style>
    </div>
  );
};

export default ClassificationsAdd;
